<script lang="ts">
  import Icon from './Icon.svelte';
  import ChevronLeft from 'lucide/dist/esm/icons/chevron-left.mjs';
  /**
   * The debug log view (IDEAS.md #8, revised R2.2): a read-only, paged table
   * over the relay's committed op log via `GET .../log` (§8.2) — the same
   * endpoint the client's catch-up reads, pointed at a human.
   *
   * Follows the browse/graveyard pattern: a button-reached view state, the
   * keyboard layer dead at the container. It renders the server's log, not a
   * surface for changing it — with ONE ratified exception (R3.3): the
   * developer seeder in its header authors real AppendTask ops, but only by
   * delegating to the container's store; the view itself still reads the
   * relay and never touches the log.
   *
   * R2.2 revisions:
   * - NEWEST ON TOP, client two-step: `/log` only reads forward (`seq > since`,
   *   ascending), but every page carries `head_seq`. A one-row probe learns
   *   head, the first read takes the newest PAGE_SIZE rows
   *   (`since = max(0, head − PAGE_SIZE)` returns head−(PAGE_SIZE−1)..head
   *   exactly), and [ Older ] reads strictly below the oldest seq shown. The
   *   accumulated ascending rows render REVERSED. Server-assigned seqs are
   *   contiguous from 1, so an older read below a visible seq is never empty.
   * - The detail column renders a per-op-type one-line summary of the payload
   *   (the old "raw payloads deliberately NOT rendered" position is revised);
   *   task ids resolve through `resolveTaskTitle` — the open notebook's
   *   projection — falling back to the uuid's last 8 characters.
   * - The notebook column is gone: `/log` is a per-notebook endpoint, so the
   *   tag rides the header once and the id on hover (`title`).
   */
  import type { LogPage, Op } from '../types/generated';
  import type { Relay } from '../storage/operations';

  interface Props {
    /** The open notebook's relay transport — the same one the store flushes through. */
    relay: Relay;
    onBack: () => void;
    /** Resolves a task id to its title in the open notebook's projection. */
    resolveTaskTitle?: (id: string) => string | null;
    /** The open notebook's tag, shown once in the header. */
    notebookTag?: string;
    /**
     * R3.3: the developer seeder — authors N fake-task AppendTask ops
     * through the store's own capture path. The view stays dumb: it holds
     * the count, the container owns the authoring. Absent (anywhere the
     * store is not at hand) the control does not render.
     */
    onSeed?: (count: number) => Promise<void>;
  }

  let { relay, onBack, resolveTaskTitle, notebookTag = '', onSeed }: Props =
    $props();

  /** How many envelopes one page of the table shows. */
  const PAGE_SIZE = 50;

  /** The accumulated rows, ascending — the oldest page is PREPENDED on [ Older ]. */
  let ops = $state<LogPage['ops']>([]);
  let headSeq = $state(0);
  /** The lowest seq currently shown; [ Older ] reads strictly below it. */
  let oldest = $state<number | null>(null);
  /** Set while a page fetch is in flight; the buttons stay aria-disabled, not dead. */
  let loading = $state(false);
  let error = $state<string | null>(null);

  /** R3.3: the seed count, one page's worth by default. */
  let seedCount = $state('25');
  /** Guard against a double-click double-seeding the notebook. */
  let seeding = $state(false);

  async function seed(): Promise<void> {
    const count = Number(seedCount);
    if (!onSeed || !Number.isInteger(count) || count < 1 || seeding) return;
    seeding = true;
    try {
      // onSeed resolves only once the ops are COMMITTED (the container awaits
      // its flush), so the reload below answers "did they land?" with the
      // relay's own answer — the same endpoint the table reads.
      await onSeed(count);
      await loadInitial();
    } finally {
      seeding = false;
    }
  }

  const canLoadOlder = $derived(oldest !== null && oldest > 1);
  /** Newest on top: the accumulated ascending rows render reversed. */
  const descending = $derived([...ops].reverse());

  async function loadInitial(): Promise<void> {
    loading = true;
    error = null;
    try {
      const probe = await relay.fetchLog(0, 1);
      headSeq = probe.head_seq;
      if (probe.head_seq === 0) return; // an empty log: nothing older than the probe
      const page = await relay.fetchLog(Math.max(0, probe.head_seq - PAGE_SIZE), PAGE_SIZE);
      ops = page.ops;
      oldest = page.ops[0]?.seq ?? null;
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
    } finally {
      loading = false;
    }
  }

  async function loadOlder(): Promise<void> {
    if (oldest === null || oldest <= 1 || loading) return;
    loading = true;
    error = null;
    try {
      // Read the PAGE_SIZE rows strictly below the oldest shown. The cap on
      // `limit` matters: when oldest−1 < PAGE_SIZE, since lands at 0 and an
      // uncapped limit would re-fetch rows already on screen (seqs are
      // contiguous, so `oldest − 1 − since` is exactly how many exist below).
      const since = Math.max(0, oldest - 1 - PAGE_SIZE);
      const limit = Math.min(PAGE_SIZE, oldest - 1 - since);
      const page = await relay.fetchLog(since, limit);
      ops = [...page.ops, ...ops];
      if (page.ops.length > 0) oldest = page.ops[0]!.seq;
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
    } finally {
      loading = false;
    }
  }

  $effect(() => {
    // The newest page loads on mount via the two-step probe.
    void loadInitial();
  });

  /** A task id as a human reference: resolved title, else the uuid's last 8 chars. */
  function ref(id: string): string {
    return resolveTaskTitle?.(id) ?? id.slice(-8);
  }

  /** The one-line payload summary, per op type — every variant of the union. */
  function describe(op: Op): string {
    switch (op.type) {
      case 'CreateNotebook':
        return op.tag ? `“${op.name}” (${op.tag})` : `“${op.name}”`;
      case 'AppendTask':
        return op.parent_task_id
          ? `${op.title} (re-entry under ${ref(op.parent_task_id)})`
          : op.title;
      case 'FinishTask':
        return `finished ${ref(op.task_id)}`;
      case 'ReenterTask': {
        const kids = op.children.map((c) => c.title).join(', ');
        return kids
          ? `${ref(op.task_id)} → ${op.children.length} child task(s): ${kids}`
          : `${ref(op.task_id)} → no child tasks`;
      }
      case 'DismissTask':
        return `dismissed ${ref(op.task_id)}`;
      case 'DismissPage':
        return `dismissed page ${op.page_id.slice(-8)}`;
      case 'RewordTask':
        return `${ref(op.task_id)} → ${op.title}`;
      case 'RestoreTask':
        return `restored ${ref(op.task_id)}`;
      case 'RenameNotebook': {
        const changed = [op.tag ? `tag ${op.tag}` : null, op.description ?? null].filter(
          (bit) => bit !== null,
        );
        return changed.length > 0
          ? `“${op.name}” (${changed.join(', ')})`
          : `“${op.name}”`;
      }
    }
  }

  const MONTHS = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];

  /** `server_time` is ms since epoch (§8.1); render it like the graveyard's dates. */
  function formatTime(ms: number): string {
    const d = new Date(ms);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }
</script>

<section class="debuglog-view" data-testid="debuglog-view">
  <header class="debuglog-header">
    <button class="action" type="button" data-testid="debuglog-back" onclick={onBack}>
      <Icon node={ChevronLeft} />
      Back
    </button>
    <!-- R2.2: the tag once here — every row is the same notebook; the id hovers. -->
    <span class="debuglog-count" title={ops[0]?.notebook_id ?? ''}>
      {ops.length} ops{#if notebookTag}&nbsp;· {notebookTag}{/if}&nbsp;· head {headSeq}
    </span>

    <!-- R3.3: the developer seeder. A dev surface authors exactly one kind of
         thing — real AppendTask ops through the store — and never pretends a
         local row write would test sync. Keyboard layer stays dead at the
         container; the button is the surface. -->
    {#if onSeed}
      <span class="debuglog-seed">
        <label class="visually-hidden" for="seed-count">Items to seed</label>
        <input
          id="seed-count"
          type="number"
          min="1"
          bind:value={seedCount}
          data-testid="seed-count"
        />
        <button
          class="action"
          type="button"
          data-testid="seed-button"
          aria-disabled={seeding}
          onclick={seed}
        >
          {seeding ? 'Seeding…' : 'Seed items'}
        </button>
      </span>
    {/if}
  </header>

  {#if error}
    <p class="debuglog-empty" data-testid="debuglog-error">{error}</p>
  {:else}
    <table class="debuglog-table" data-testid="debuglog-table">
      <thead>
        <tr>
          <th scope="col">seq</th>
          <th scope="col">op</th>
          <th scope="col">detail</th>
          <th scope="col">timestamp</th>
        </tr>
      </thead>
      <tbody>
        {#each descending as envelope (envelope.seq)}
          <tr data-testid="debuglog-row">
            <td class="debuglog-seq">{envelope.seq}</td>
            <td>{envelope.op.type}</td>
            <td class="debuglog-detail">{describe(envelope.op)}</td>
            <td class="debuglog-time">{formatTime(envelope.server_time)}</td>
          </tr>
        {/each}
      </tbody>
    </table>

    {#if ops.length === 0 && !loading}
      <p class="debuglog-empty" data-testid="debuglog-empty">No ops committed yet.</p>
    {/if}
  {/if}

  <footer class="debuglog-footer">
    <!--
      `aria-disabled` rather than `disabled`: the button stays focusable so a
      keyboard user can reach it and hear that there is nothing older. Seqs
      are contiguous from 1, so `oldest > 1` is exactly "rows exist below".
    -->
    <button
      class="action"
      type="button"
      data-testid="debuglog-older"
      aria-disabled={!canLoadOlder || loading}
      onclick={() => (canLoadOlder && !loading ? void loadOlder() : undefined)}
    >
      [ Older ]
    </button>
  </footer>
</section>

<style>
  .debuglog-view {
    width: min(100%, 60em);
    margin: 0 auto;
    padding: 0.75rem 0 3rem;
  }

  .debuglog-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;
  }

  .debuglog-count {
    font-size: 12px;
    color: var(--ink-muted);
    white-space: nowrap;
  }

  /* R3.3: the seeder parks at the right end of the header row. */
  .debuglog-seed {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .debuglog-seed input {
    width: 4em;
  }

  .debuglog-table {
    width: 100%;
    border-collapse: collapse;
    background: var(--paper);
    box-shadow: var(--shadow-panel);
    font-size: 14px;
  }

  .debuglog-table th {
    text-align: left;
    font-weight: normal;
    font-size: 12px;
    color: var(--ink-muted);
    padding: 0.5rem 1rem;
    border-bottom: 1px solid color-mix(in srgb, var(--ink-muted) 40%, transparent);
  }

  .debuglog-table td {
    padding: 0.45rem 1rem;
    border-bottom: 1px solid color-mix(in srgb, var(--ink-muted) 18%, transparent);
    font-family: var(--font-mono, monospace);
    color: var(--ink);
  }

  .debuglog-seq {
    text-align: right;
    width: 4rem;
  }

  .debuglog-detail {
    max-width: 28em;
    overflow-wrap: anywhere;
  }

  .debuglog-time {
    white-space: nowrap;
    font-family: var(--font-serif, serif);
  }

  .debuglog-empty {
    color: var(--ink-muted);
    font-style: italic;
  }

  .debuglog-footer {
    display: flex;
    justify-content: center;
    margin-top: 1rem;
  }
</style>
